import { useState } from "react";
import { ShieldAlert, TriangleAlert } from "lucide-react";
import { useAdminApi } from "../lib/api";
import { useI18n, serverError } from "../i18n";

/**
 * 詐騙訊號的審核佇列
 * ═══════════════════════════════════════════════════════════════
 *
 * ── 和檢舉佇列分開的理由 ────────────────────────────────────
 *
 * 檢舉是一個**人**說「這個有問題」；訊號是一段規則比對到了字面
 * 模式。前者要看的是指控是否成立，後者要看的是規則有沒有誤判 ——
 * 那是兩種不同的判斷，而混在一起會讓兩種都做不好。
 *
 * ── 這一頁最重要的東西是「片段」 ────────────────────────────
 *
 * 一個訊號名稱（advanceFee）什麼都沒說。審核者要判斷的是脈絡：
 * 「訂金」兩個字在正常案件裡很常見，「錄取後請先繳交保證金 3000 元」
 * 不是。所以每一個訊號都帶著命中位置的前後文。
 *
 * 沒有片段的話，審核者得自己去翻原始內容 —— 而那個摩擦會讓
 * 佇列積起來，然後所有訊號一起失去意義。
 *
 * ── 為什麼駁回也要寫理由 ────────────────────────────────────
 *
 * 誤判率是唯一能告訴我們規則寫太鬆的資料，而那份資料只存在於
 * 被駁回的那些紀錄裡。沒有理由的駁回等於把它丟掉。
 */

interface Signal {
  name: string;
  weight: number;
  excerpt: string;
}

interface Flag {
  id: string;
  market: string;
  targetKind: string;
  targetId: string;
  authorId: string | null;
  score: number;
  signals: Signal[];
  status: string;
  createdAt: string;
  resolution: string | null;
  authorEmail: string | null;
  authorName: string | null;
  authorSuspendedAt: string | null;
}

interface Page {
  flags: Flag[];
  total: number;
}

type Status = "open" | "dismissed" | "actioned" | "all";
const TABS: Status[] = ["open", "actioned", "dismissed", "all"];

export default function Fraud() {
  const { t } = useI18n();
  const [status, setStatus] = useState<Status>("open");
  const [done, setDone] = useState<Record<string, string>>({});

  const { data, error, loading } = useAdminApi<Page>(`/fraud-flags?status=${status}`, [status]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="flex items-center gap-2 text-lg font-semibold text-ink">
          <ShieldAlert className="size-5 text-brand-300" />
          {t("fr.title")}
        </h1>
        <p className="mt-1.5 text-xs leading-relaxed text-ink-muted">{t("fr.lede")}</p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {TABS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            className={
              "rounded-pill px-3.5 py-1.5 text-xs font-medium transition " +
              (status === s ? "nm-inset text-ink" : "text-ink-muted hover:text-ink")
            }
          >
            {t(`fr.tab.${s}` as never)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[0, 1].map((i) => (
            <div key={i} className="h-36 rounded-card shimmer" />
          ))}
        </div>
      ) : error ? (
        <p className="rounded-card nm-inset p-5 text-sm text-brand-300">{error}</p>
      ) : !data || data.flags.length === 0 ? (
        <p className="rounded-card nm-inset p-8 text-center text-sm text-ink-muted">
          {t("fr.empty")}
        </p>
      ) : (
        <div className="space-y-3">
          {data.flags.map((flag) => (
            <Row
              key={flag.id}
              flag={flag}
              resolved={done[flag.id]}
              onResolved={(next) => setDone((d) => ({ ...d, [flag.id]: next }))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Row({
  flag,
  resolved,
  onResolved,
}: {
  flag: Flag;
  resolved?: string;
  onResolved: (status: string) => void;
}) {
  const { t } = useI18n();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function resolve(status: "dismissed" | "actioned") {
    if (!note.trim()) {
      setError(t("fr.needNote"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/fraud-flags/${flag.id}/resolve`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ status, resolution: note.trim() }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        setError(serverError(payload, t, t("fr.failed")));
        return;
      }
      onResolved(status);
    } catch {
      setError(t("fr.network"));
    } finally {
      setBusy(false);
    }
  }

  const status = resolved ?? flag.status;
  const settled = status !== "open";

  return (
    <article className="rounded-card nm p-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/*
            分數用顏色分級。

            60 以上是 priority（規則的門檻），30 到 59 是 review。
            用同一組門檻是刻意的 —— 後台顯示的分級和伺服器端的
            判斷若不一致，審核者會建立錯誤的直覺。
          */}
          <span
            className={
              "flex items-center gap-1 rounded-pill px-2.5 py-1 text-[11px] font-semibold " +
              (flag.score >= 60
                ? "bg-brand-400/15 text-brand-300"
                : "nm-inset text-ink-soft")
            }
          >
            <TriangleAlert className="size-3" />
            {flag.score}
          </span>
          <span className="rounded-pill nm-inset px-2.5 py-1 text-[11px] text-ink-muted">
            {t(`fr.kind.${flag.targetKind}` as never)}
          </span>
          <span className="rounded-pill nm-inset px-2.5 py-1 text-[11px] text-ink-muted">
            {flag.market}
          </span>
          {flag.authorSuspendedAt && (
            <span className="rounded-pill nm-inset px-2.5 py-1 text-[11px] text-ink-muted">
              {t("fr.alreadySuspended")}
            </span>
          )}
        </div>

        <time className="shrink-0 text-[11px] text-ink-muted">
          {new Date(flag.createdAt).toLocaleString()}
        </time>
      </header>

      <p className="mt-3 text-xs text-ink-muted">
        {t("fr.author")}{" "}
        <span className="text-ink-soft">
          {flag.authorName ?? flag.authorEmail ?? flag.authorId ?? "—"}
        </span>
        {"　"}
        {t("fr.target")} <span className="font-mono text-ink-soft">{flag.targetId}</span>
      </p>

      {/*
        訊號與片段。這是整頁最重要的部分 —— 訊號名稱說不出任何
        東西，片段才讓審核者看得到脈絡。
      */}
      <ul className="mt-3 space-y-2">
        {flag.signals.map((s, i) => (
          <li key={`${s.name}-${i}`} className="rounded-xl nm-inset px-4 py-2.5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-[11px] font-medium text-ink">
                {t(`fr.signal.${s.name}` as never)}
              </span>
              <span className="shrink-0 text-[11px] tabular-nums text-ink-muted">+{s.weight}</span>
            </div>
            {/*
              私人訊息的訊號沒有片段。

              案件描述是公開文字，存片段沒有額外的隱私成本；
              私訊不是 —— 存片段等於讓管理員讀到一段私人對話，
              而且沒有稽核紀錄、當事人也不知道。

              顯示一句說明而不是一個空白區塊：空白看起來像
              「壞掉了」，而這裡是「刻意沒有」。
            */}
            {s.excerpt ? (
              <p className="mt-1 break-words text-[11px] leading-relaxed text-ink-soft">
                {s.excerpt}
              </p>
            ) : (
              <p className="mt-1 text-[11px] italic leading-relaxed text-ink-muted">
                {t("fr.excerptWithheld")}
              </p>
            )}
          </li>
        ))}
      </ul>

      {settled ? (
        <p className="mt-3 rounded-xl nm-inset px-4 py-3 text-xs text-ink-muted">
          {t(`fr.tab.${status}` as never)}
          {flag.resolution ? ` — ${flag.resolution}` : ""}
        </p>
      ) : (
        <div className="mt-4 space-y-2.5">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={1000}
            placeholder={t("fr.notePlaceholder")}
            className="w-full resize-y rounded-xl nm-inset bg-transparent px-4 py-2.5 text-xs text-ink outline-none focus:ring-1 focus:ring-brand-400/40"
          />

          {error && (
            <p role="alert" className="text-[11px] text-brand-300">
              {error}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => resolve("actioned")}
              disabled={busy}
              className="rounded-pill nm-pressable px-4 py-2 text-xs font-semibold text-ink disabled:opacity-40"
            >
              {t("fr.action.actioned")}
            </button>
            <button
              type="button"
              onClick={() => resolve("dismissed")}
              disabled={busy}
              className="rounded-pill nm-pressable px-4 py-2 text-xs font-medium text-ink-soft disabled:opacity-40"
            >
              {t("fr.action.dismissed")}
            </button>
          </div>

          {/*
            這一句是給審核者看的，不是文案裝飾。

            誤判率是唯一能告訴我們規則寫太鬆的資料，而那份資料
            只存在於被駁回的紀錄裡。
          */}
          <p className="text-[11px] leading-relaxed text-ink-muted">{t("fr.whyNote")}</p>
        </div>
      )}
    </article>
  );
}
